import { useEffect, useState } from 'react';
import { liensPublicsApi } from '../../api/resources';
import { QRCode } from '../../components/QRCode.jsx';
import { Bar } from '../../components/ui/Bar.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { numeroInternational } from '../../lib/envoi';
import { tr, trf } from '../../lib/i18n';
import { C } from '../../theme/palette';

const bouton = (fond) => ({
  background: fond,
  color: '#fff',
  borderRadius: 9,
  padding: 10,
  textAlign: 'center',
  fontSize: 10,
  fontWeight: 700,
});

/** « Ouvert 2 fois », d'après ce que le serveur a compté. */
function consultations(lien) {
  if (!lien?.consultations) return 'Pas encore ouvert par le client';
  return 'Ouvert ' + lien.consultations + ' fois par le client';
}

/**
 * Message remis au client avec son lien. Il s'adresse au client final : le
 * vouvoiement est voulu, et il part dans la langue de l'entreprise.
 *
 * @param {object} [client] client du chantier
 * @param {string} url adresse du portail
 * @returns {string} le message
 */
function messagePortail(client, url) {
  return trf(
    'Bonjour {0}, suivez l’avancement de votre chantier en direct (photos incluses) : {1}',
    client?.nom || '',
    url,
  );
}

const etiquette = {
  fontSize: 8,
  fontWeight: 800,
  color: C.muted,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  margin: '12px 0 5px',
};

/**
 * Onglet « Portail » d'un chantier : le lien par lequel le client suit son
 * chantier, et son état.
 *
 * <p>La maquette composait le lien à l'écran, du numéro du chantier et de celui
 * du client, sur un domaine qui n'est pas celui de l'installation : il ne menait
 * nulle part et se devinait. Le lien vient du serveur, avec un jeton tiré au
 * hasard. À la différence du lien d'une pièce, le portail dure tant que le
 * chantier : il s'active et se désactive d'un geste, et l'onglet dit lequel des
 * deux il est sans rien rouvrir par le fait de le lire.
 *
 * @param {object} props propriétés du composant
 * @param {object} props.chantier chantier affiché
 * @param {object} [props.client] client du chantier
 * @param {import('react').ReactNode} [props.children] ce que le client voit, affiché sous le lien
 * @returns {JSX.Element} la carte
 */
export function PortailClient({ chantier, client, children }) {
  // `chargement` tant que le serveur n'a pas répondu, puis `actif`,
  // `inactif`, ou `injoignable` si la demande a échoué.
  const [etat, setEtat] = useState('chargement');
  const [lien, setLien] = useState(null);
  const [copie, setCopie] = useState(false);
  const [occupe, setOccupe] = useState(false);
  const enregistre = typeof chantier?.id === 'number';
  const id = chantier?.id;

  useEffect(() => {
    if (!enregistre) {
      setEtat('inactif');
      return undefined;
    }
    let annule = false;
    setEtat('chargement');
    liensPublicsApi
      .existant('chantier', id)
      .then((trouve) => {
        if (annule) return;
        // Sans lien, le serveur répond vide : l'onglet dit « désactivé ».
        setLien(trouve || null);
        setEtat(trouve ? 'actif' : 'inactif');
      })
      .catch((e) => {
        if (annule) return;
        setLien(null);
        setEtat(e?.estIntrouvable ? 'inactif' : 'injoignable');
      });
    return () => {
      annule = true;
    };
  }, [enregistre, id]);

  const active = async () => {
    setOccupe(true);
    try {
      const cree = await liensPublicsApi.pour('chantier', id);
      setLien(cree);
      setEtat('actif');
    } catch (e) {
      setEtat('injoignable');
    } finally {
      setOccupe(false);
    }
  };

  const desactive = async () => {
    if (
      !window.confirm(
        'Désactiver le lien ? Le client ne pourra plus ouvrir la page de suivi. Tu pourras en activer un nouveau ensuite.',
      )
    )
      return;
    setOccupe(true);
    try {
      await liensPublicsApi.revoque('chantier', id);
      setLien(null);
      setEtat('inactif');
    } catch (e) {
      setEtat('injoignable');
    } finally {
      setOccupe(false);
    }
  };

  const texte = lien ? messagePortail(client, lien.url) : '';
  const actif = etat === 'actif' && lien;

  return (
    <Card>
      <Bar
        icon="🔗"
        title="Portail client — suivi du chantier"
        color={C.teal}
        sub="Lien unique pour ce chantier et ce client"
      />
      <div style={{ padding: 13 }}>
        <div
          role="status"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 9,
            fontWeight: 800,
            color: actif ? C.green : C.muted,
            marginBottom: 8,
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 8,
              height: 8,
              borderRadius: 99,
              background: actif ? C.green : C.border,
              display: 'inline-block',
            }}
          />
          {etat === 'chargement'
            ? 'Vérification du lien…'
            : actif
              ? 'Lien actif'
              : etat === 'injoignable'
                ? 'Lien indisponible pour le moment'
                : 'Lien désactivé'}
        </div>

        {etat === 'injoignable' && (
          <div style={{ fontSize: 8.5, color: C.muted, lineHeight: 1.5, marginBottom: 8 }}>
            Le serveur n’a pas répondu. Recharge la page pour réessayer.
          </div>
        )}

        {!actif && etat !== 'chargement' && (
          <div>
            <div style={{ fontSize: 8.5, color: C.muted, lineHeight: 1.5, marginBottom: 8 }}>
              {enregistre
                ? 'Active le lien pour que ' +
                  (client?.nom || 'le client') +
                  ' suive son chantier : avancement, dates, postes et les photos que tu choisis ci-dessous.'
                : 'Le lien sera disponible dès que le chantier sera enregistré.'}
            </div>
            <Btn
              label={occupe ? '…' : '🌐 Activer le lien'}
              sm
              color={C.teal}
              full
              disabled={!enregistre || occupe}
              onClick={active}
            />
          </div>
        )}

        {actif && (
          <>
            <div style={{ fontSize: 8, color: C.muted, marginBottom: 4 }}>
              Lien de suivi de <b>{client?.nom || 'ce client'}</b> · chantier{' '}
              {chantier.code || chantier.id} · {consultations(lien)}
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 8 }}>
              <div
                style={{
                  flex: 1,
                  background: C.navyXL,
                  borderRadius: 9,
                  padding: '10px 12px',
                  fontSize: 9,
                  color: C.grey,
                  wordBreak: 'break-all',
                  fontFamily: 'monospace',
                  minWidth: 0,
                }}
              >
                {lien.url}
              </div>
              <div title="QR du lien de suivi" style={{ flexShrink: 0 }}>
                <QRCode value={lien.url} size={76} />
              </div>
            </div>
            <div style={{ marginBottom: 8 }}>
              <Btn
                label={copie ? '✓ Lien copié' : '📋 Copier le lien'}
                sm
                color={copie ? C.green : C.teal}
                full
                onClick={() => {
                  try {
                    navigator.clipboard && navigator.clipboard.writeText(lien.url);
                  } catch (e) {
                    // Presse-papiers refusé : le lien reste lisible à l'écran.
                  }
                  setCopie(true);
                  setTimeout(() => setCopie(false), 1500);
                }}
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <a
                href={
                  'mailto:' +
                  (client?.email || '') +
                  '?subject=' +
                  encodeURIComponent(tr('Suivi de votre chantier')) +
                  '&body=' +
                  encodeURIComponent(texte)
                }
                style={{ textDecoration: 'none' }}
              >
                <div style={bouton(C.navy)}>✉ Email</div>
              </a>
              <a
                href={
                  'https://wa.me/' +
                  numeroInternational(client?.tel) +
                  '?text=' +
                  encodeURIComponent(texte)
                }
                target="_blank"
                rel="noreferrer"
                style={{ textDecoration: 'none' }}
              >
                <div style={bouton(C.green)}>WhatsApp</div>
              </a>
            </div>
            <div style={etiquette}>Ce que reçoit le client</div>
            <div
              data-testid="message-client"
              style={{
                background: '#DCF8C6',
                borderRadius: '10px 10px 2px 10px',
                padding: '8px 10px',
                fontSize: 9,
                color: C.ink,
                lineHeight: 1.5,
                wordBreak: 'break-word',
                marginLeft: 24,
              }}
            >
              {texte}
            </div>
            <div style={{ fontSize: 8, color: C.muted, marginTop: 5, lineHeight: 1.5 }}>
              En touchant le lien, le client ouvre la page de suivi, sans compte ni mot de passe.
            </div>
            <a
              href={lien.url}
              target="_blank"
              rel="noreferrer"
              style={{ textDecoration: 'none', display: 'block', marginTop: 8 }}
            >
              <div style={bouton(C.ink)}>👁 Voir la page comme le client</div>
            </a>
            <div style={{ fontSize: 8, color: C.muted, marginTop: 5, lineHeight: 1.5 }}>
              Quand c’est toi qui l’ouvres, ça ne compte pas comme une visite du client.
            </div>
            <div style={{ marginTop: 8 }}>
              <Btn
                label={occupe ? '…' : '🚫 Désactiver le lien'}
                sm
                ghost
                color={C.grey}
                full
                disabled={occupe}
                onClick={desactive}
              />
              <div style={{ fontSize: 8, color: C.muted, marginTop: 5, lineHeight: 1.5 }}>
                Le lien désactivé cesse de s’ouvrir aussitôt, pour le client comme pour ses photos.
              </div>
            </div>
          </>
        )}

        {children}
      </div>
    </Card>
  );
}
